import React, { ChangeEvent, Fragment, FunctionComponent, useEffect, useState } from "react";
import * as ReactDOM from 'react-dom/client';
import { Col, Modal, Button, Row, Toast, ToastContainer, FormCheck } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrashCan } from '@fortawesome/free-regular-svg-icons';


export function startPage(rootSelector: string, bookings: Booking[]) {
    const domContainer = document.querySelector(rootSelector);
    if (!domContainer) {
        return;
    }
    const root = ReactDOM.createRoot(domContainer);
    root.render(React.createElement(BookingList, { bookings }));
}

interface Booking {
    EntryNo: number;
    CustomerNo: string;
    CustomerName: string;
    VehicleRego: string;
    VehicleType: string;
    VehicleNo: string;
    BookingType: string;
    ContractRefNo: string;
    MaterialType: string;
    MaterialCode: string;
    CustomerPoNo: string;
    BookingDate: ServerDate;
}

interface ServerDate {
    Year: number;
    Month: number;
    Day: number;
}

interface Props {
    bookings: Booking[];
}

enum ModalShow {
    None,
    Edit,
    Delete
}

interface UpdateBookingRequest {
    EntryNo: number;
    NewRego: string;
}

interface DeleteBookingsRequest {
    Bookings: number[]
}

interface SaveBookingResponse {
    success: boolean;
    errorMessage: string;
}

const BookingList: FunctionComponent<Props> = ({ bookings }) => {
    const [bookingList, setBookingList] = useState<Booking[]>(bookings);
    const [modalShow, setModalShow] = useState<{ type: ModalShow, currentBooking?: Booking }>({ type: ModalShow.None });
    const [newRego, setNewRego] = useState("");
    const [submitRequest, setSubmitRequest] = useState<{ url: string, requestInfo: RequestInit, onSuccess: () => void } | null>(null);
    const [showToast, setShowToast] = useState(false);
    const [validationError, setValidationError] = useState("");

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        if (submitRequest) {
            return;
        }

        const currentBooking = modalShow.currentBooking;
        let onSuccess: () => void;
        switch (modalShow.type) {
            case ModalShow.Edit: {
                if (!currentBooking?.EntryNo) {
                    return;
                }
                const request: UpdateBookingRequest = {
                    EntryNo: currentBooking.EntryNo,
                    NewRego: newRego
                }
                onSuccess = () => {
                    const updatedBookings = bookingList.map(booking => {
                        if (booking.EntryNo == currentBooking.EntryNo) {
                            return { ...booking, VehicleRego: newRego }
                        }
                        return booking;
                    });
                    setBookingList(updatedBookings);
                };
                setSubmitRequest({ url: "/api/BookingApi/UpdateBooking", requestInfo: createRequestData(JSON.stringify(request)), onSuccess });
                break;
            }
            case ModalShow.Delete: {
                if (!currentBooking?.EntryNo) {
                    return;
                }
                const request: DeleteBookingsRequest = {
                    Bookings: [currentBooking.EntryNo]
                }
                onSuccess = () => {
                    setBookingList(bookingList.filter(booking => booking.EntryNo !== currentBooking.EntryNo));
                };
                setSubmitRequest({ url: "/api/BookingApi/DeleteBooking", requestInfo: createRequestData(JSON.stringify(request)), onSuccess });
                break;
            }
            default:
                throw Error("unknown modal");
        }
    }

    const createRequestData = (requestString: string) => {
        const requestInfo = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: requestString,
        }
        return requestInfo;
    }

    useEffect(() => {
        if (!submitRequest) {
            return;
        }

        const fetchData = async () => {
            setValidationError("");
            await fetch(submitRequest.url, submitRequest.requestInfo)
                .then((response) => response.json())
                .then((data: SaveBookingResponse) => {
                    console.log(data);
                    if (data.success) {
                        submitRequest.onSuccess();
                    } else {
                        setValidationError(data.errorMessage)
                    }
                })
                .catch((error) => {
                    console.error(error);
                    setValidationError(error)
                });
            resetState();
            setShowToast(true);
            setSubmitRequest(null);
        };
        fetchData();
    }, [submitRequest]);

    const resetState = () => {
        setModalShow({ type: ModalShow.None, currentBooking: undefined });
        setNewRego("");
    }

    return (
        <Fragment>
            <table className="table table-striped">
                <thead>
                    <tr>
                        <th className="label" scope="col">Booking Date</th>
                        <th className="label" scope="col">Contract Ref No.</th>
                        {/* <th className="label" scope="col">Contract Description</th> */}
                        <th className="label" scope="col">Material Type</th>
                        <th className="label" scope="col">Vehicle Rego</th>
                        <th className="label" scope="col">Vehicle Type</th>
                        <th className="label" scope="col">Purchase Order/Customer Ref No.</th>
                        <th className="label" scope="col"></th>
                        <th className="label" scope="col"></th>
                    </tr>
                </thead>
                <tbody>
                    {
                        bookingList.length == 0 ? (<tr><td colSpan={9}>There are no results within the filter</td></tr>) :
                            bookingList.map((booking, i) => (
                                <tr key={i}>
                                    {/* @* Note: increase colspan of 'No results' row when adding new columns *@ */}
                                    <td className="value">{getBookingDate(booking.BookingDate)}</td>
                                    <td className="value">{booking.ContractRefNo}</td>
                                    {/* @* todo put contract description here *@ */}
                                    {/* @* <td className="value">@booking.ContractRefNo</td>  *@ */}
                                    <td className="value">{booking.MaterialType}</td>
                                    <td className="value">{booking.VehicleRego}</td>
                                    <td className="value">{booking.VehicleType}</td>
                                    <td className="value">{booking.CustomerPoNo}</td>
                                    <td className="value"><button className="site-button small" onClick={() => {
                                        if (!submitRequest) {
                                            setModalShow({ type: ModalShow.Edit, currentBooking: booking });
                                            setNewRego(booking.VehicleRego);
                                        }
                                    }}><FontAwesomeIcon icon={faPenToSquare} /></button></td>
                                    <td className="value"><button className="site-button small" onClick={() => {
                                        if (!submitRequest) {
                                            setModalShow({ type: ModalShow.Delete, currentBooking: booking });
                                        }
                                    }}><FontAwesomeIcon icon={faTrashCan} /></button></td>
                                    {/* @* Note: increase colspan of 'No results' row when adding new columns *@ */}
                                </tr>
                            ))
                    }
                </tbody>
            </table>
            <Modal show={modalShow.type != ModalShow.None} className="booking-history-modal" size="lg" aria-labelledby="contained-modal-title-vcenter" centered>
                <form onSubmit={handleSubmit}>
                    <Modal.Header>
                        {(() => {
                            switch (modalShow.type) {
                                case ModalShow.Edit: return <Modal.Title id="contained-modal-title-vcenter">Edit Booking</Modal.Title>;
                                case ModalShow.Delete: return <Modal.Title id="contained-modal-title-vcenter">Delete Booking</Modal.Title>;
                            }
                        })()}
                    </Modal.Header>
                    <Modal.Body>
                        {(() => {
                            switch (modalShow.type) {
                                case ModalShow.Edit: return <div>
                                    <label htmlFor="vehicle-rego">New Vehicle Rego</label>
                                    <input className="form-control" style={{ maxWidth: "300px" }} name={"vehicle-rego"} value={newRego} onChange={(e) => (setNewRego(e.target.value))} maxLength={20}></input>
                                </div>
                                case ModalShow.Delete: return <p>Are you sure you would like to delete this booking?</p>;
                            }
                        })()}

                    </Modal.Body>
                    <Modal.Footer>
                        <button type="submit" className="site-button small" onClick={() => { }}>{modalShow.type == ModalShow.Edit ? "Save" : "Confirm"}</button>
                        <button id="cancel-button" type="button" className="site-button secondary-button small" onClick={() => {
                            setModalShow({ type: ModalShow.None, currentBooking: undefined });
                        }}>Cancel</button>
                    </Modal.Footer>
                </form>
            </Modal>
            <ToastContainer className="p-3" position="top-center">
                <Toast onClose={() => setShowToast(false)} show={showToast} delay={3000} autohide={validationError == ""} style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}>
                    <Toast.Header>
                        <strong className="me-auto">{validationError != "" ? "Failed to update booking" : "Success"}</strong>
                        <small>now</small>
                    </Toast.Header>
                    <Toast.Body>{validationError != "" ? validationError : "Changes have been saved successfully."}</Toast.Body>
                </Toast>
            </ToastContainer>
        </Fragment>
    )
}
const getBookingDate = (serverDate: ServerDate) => {
    if (serverDate.Year == 1) {
        return "";
    } else {
        return `${serverDate.Day}/${serverDate.Month}/${serverDate.Year}`;
    }
}